import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote,
  Link2, Undo, Redo, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
} from "lucide-react";

const Btn = ({ onClick, active, title, children, testid }) => (
  <button
    type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`h-8 min-w-8 px-2 rounded-md text-sm flex items-center justify-center transition-colors ${
      active ? "bg-emerald-800 text-white" : "bg-white text-emerald-900 hover:bg-emerald-50 border border-stone-200"
    }`}
  >{children}</button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Start writing..." }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const H = (level, Icon) => (
    <Btn title={`Heading ${level}`} testid={`rte-h${level}`}
      active={editor.isActive("heading", { level })}
      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}>
      {Icon ? <Icon size={16} /> : `H${level}`}
    </Btn>
  );

  return (
    <div className="tiptap-editor rounded-xl border border-stone-200 bg-white overflow-hidden">
      <div className="flex flex-wrap gap-1 p-2 border-b border-stone-200 bg-stone-50 sticky top-0 z-10">
        {H(1, Heading1)}{H(2, Heading2)}{H(3, Heading3)}{H(4)}{H(5)}{H(6)}
        <span className="w-px bg-stone-300 mx-1" />
        <Btn title="Bold" testid="rte-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></Btn>
        <Btn title="Italic" testid="rte-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></Btn>
        <Btn title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></Btn>
        <span className="w-px bg-stone-300 mx-1" />
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></Btn>
        <Btn title="Ordered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></Btn>
        <Btn title="Link" onClick={addLink} active={editor.isActive("link")}><Link2 size={16} /></Btn>
        <span className="w-px bg-stone-300 mx-1" />
        <Btn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={16} /></Btn>
        <Btn title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={16} /></Btn>
        <Btn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={16} /></Btn>
        <span className="w-px bg-stone-300 mx-1" />
        <Btn title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo size={16} /></Btn>
        <Btn title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo size={16} /></Btn>
      </div>
      <EditorContent editor={editor} className="prose-hr p-4" data-testid="rte-content" />
    </div>
  );
}
